import React from 'react';
import PropTypes from 'prop-types';
import Modal, { ModalActions } from '../Modal';
import Button, { ButtonGroup } from '../Button';

export default class ExternalLink extends React.Component {
    static propTypes = {
        /** Contents of the modal */
        children: PropTypes.node.isRequired,

        /** Additional className applied to the wrapping element */
        className: PropTypes.string,

        /** External destination URL */
        url: PropTypes.string.isRequired,

        /** Include the external link icon */
        hasExternalIcon: PropTypes.bool,

        /** Modify the copy of the Continue button within the modal */
        continueButtonCopy: PropTypes.string,

        /** Modify the copy of the Cancel button within the modal */
        cancelButtonCopy: PropTypes.string,

        /** Modify the copy of the modal Header */
        modalHeading: PropTypes.string,

        /** Set the size of the modal */
        modalSize: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'full']),
    };

    static defaultProps = {
        className: '',
        hasExternalIcon: true,
        continueButtonCopy: 'Continue to External Web Site',
        cancelButtonCopy: 'Cancel',
        modalHeading: 'You are leaving Recreation.gov',
        modalSize: 'lg',
    };

    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
        };
    }

    onClickLink = (e) => {
        e.preventDefault();
        this.setState({
            isOpen: true,
        });
    };

    onModalCancel = () => {
        this.setState({
            isOpen: false,
        });
    };

    onModalContinue = () => {
        // Create a temporary link element that will set `noopener` and `noreferrer`,
        // then manually fire its click handler.
        Object.assign(document.createElement('a'), {
            target: '_blank',
            href: this.props.url,
            rel: 'noopener noreferrer',
        }).click();
        this.setState({
            isOpen: false,
        });
    };

    render() {
        const {
            modalHeading,
            className,
            continueButtonCopy,
            cancelButtonCopy,
            hasExternalIcon,
            url,
            modalSize,
            children,
        } = this.props;

        // Keeping the CONTINUE_TEXT and CANCEL_TEXT const to avoid and potential issues
        // with the existing version of this component being used across the site.
        const CONTINUE_TEXT = continueButtonCopy;
        const CANCEL_TEXT = cancelButtonCopy;
        const hasExternalIconClass = hasExternalIcon ? '' : 'sarsa-button-external-link';

        return (
            <React.Fragment>
                <a
                    className={`${className} ${hasExternalIconClass}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={this.onClickLink}
                >
                    {children}
                </a>
                {this.state.isOpen && (
                    <Modal
                        size={modalSize}
                        isOpen={this.state.isOpen}
                        heading={modalHeading}
                        onRequestClose={this.onModalCancel}
                        shouldShowCloseButton
                        shouldActionsStickInMobile
                    >
                        <p>
                            You are leaving the{' '}
                            <a href="https://www.recreation.gov">Recreation.gov</a>{' '}
                            website and entering an external or third-party site.&nbsp;
                            <a href="https://www.recreation.gov">Recreation.gov</a> has
                            provided this link because it contains relevant information
                            that may be useful or interesting. This does not constitute
                            endorsement, recommendation, or favoring by{' '}
                            <a href="https://www.recreation.gov">Recreation.gov</a> or the
                            United States Government.{' '}
                            <a href="https://www.recreation.gov">Recreation.gov</a> has
                            provided these external links for reference only and cannot
                            attest to the accuracy of information provided by third-party
                            sites or any other linked site outside the control of&nbsp;
                            <a href="https://www.recreation.gov">Recreation.gov</a>.
                        </p>

                        <ModalActions>
                            <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                                <Button
                                    appearance="tertiary"
                                    onClick={this.onModalCancel}
                                >
                                    {CANCEL_TEXT}
                                </Button>
                                <Button
                                    aria-label={CONTINUE_TEXT}
                                    onClick={this.onModalContinue}
                                >
                                    {CONTINUE_TEXT}
                                </Button>
                            </ButtonGroup>
                        </ModalActions>
                    </Modal>
                )}
            </React.Fragment>
        );
    }
}
